import { Component, OnInit } from '@angular/core';

import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { HTTPService, HelperService } from 'app/services';

import { itemTypeIcons, itemTypeLabel } from 'app/constants/itemType.constants';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components';

import { debounce, find, findIndex, trim } from 'lodash';

@Component({
  selector: 'app-sales-code-assign-dialog',
  templateUrl: './sales-code-assign-dialog.component.html',
  styleUrls: ['./sales-code-assign-dialog.component.scss'],
})
export class SalesCodeAssignDialogComponent implements OnInit {
  typeMap: any = itemTypeLabel;
  iconMap: any = itemTypeIcons;
  skippedItems: any = {};
  itemName: string = '';
  searchQuery: string = '';
  salesGroupsResults: any = [];
  groupedSalesResults: any = [];
  orderId: string = '';
  isShowMultipleAssignView: boolean = false;
  isLoading: boolean = true;
  unassignedServices: any = [];
  handleSearch = debounce(this._searchSalesGroup, 400);
  selectedItem: any = {};
  isLoadingSearch: boolean = false;
  totalRecords: number = 0;
  pageSize: number = 100;
  originalItemObject: any = {};
  salesCodeAssignedItemList: any[] = [];
  openFrom: string = '';
  isSubmitting: boolean = false;
  isAutoAssign: boolean = false;
  isFocusSearch: boolean = true;

  constructor(
    private dynamicDialogRef: DynamicDialogRef,
    private dialogService: DialogService,
    private dynamicDialogConfig: DynamicDialogConfig,
    private httpService: HTTPService,
    private helperService: HelperService,
  ) {}

  ngOnInit(): void {
    this.getData();
    const { isShowMultipleAssignView = false, orderId = '', itemName = '', unassignedServices = [], skippedItems = {}, item = {}, openFrom = '' } = this.dynamicDialogConfig.data;
    this.isShowMultipleAssignView = isShowMultipleAssignView;
    this.orderId = orderId;
    this.itemName = itemName;
    this.unassignedServices = this.getUnassignedItemsServices(unassignedServices);
    this.skippedItems = this.getUnassigedSkippedItems(skippedItems);
    this.originalItemObject = item;
    this.openFrom = openFrom;

    if (this.unassignedServices?.length) {
      if (this.unassignedServices[0]?.items?.length) {
        this.setNextSelectedItem(this.unassignedServices[0]?.items[0]);
      }
    }
  }

  logPXGaingSightCustomEvent(res: any) {
    const logInfo = {
      itemName: res?.title || res?.name,
      salesCodeName: res?.salesCode?.title,
      salesCode: res?.salesCode?.shortName,
      category: res?.category?.name,
      subCategory: res?.category?.subCategoryName,
      itemType: res?.type,
      assignmentType: this.isAutoAssign
        ? 'Auto'
        : this.isShowMultipleAssignView
          ? 'User has assigned Sales Code from assign all sales code flow on RO'
          : 'User has assigned sales code directly from the Catalog/RO',
    };
    this.helperService.logPXGaingSightEvent('Sales code assigned to an item', logInfo);
  }

  getUnassigedSkippedItems(skippedItems: any): any {
    const skippedItemsObj: any = {};
    const list = Object.values(skippedItems);
    if (list?.length) {
      list.forEach((item: any) => {
        if (!item?.salesCode?.salesCodeId) {
          skippedItemsObj[item.itemId] = item;
        }
      });
    }

    return skippedItemsObj;
  }

  getUnassignedItemsServices(unassignedServices: any[]): any[] {
    let services: any[] = [];
    if (unassignedServices?.length) {
      unassignedServices?.forEach((service: any) => {
        const items = service.items.filter((item: any) => !item?.salesCode?.salesCodeId);
        if (items?.length) {
          services.push({
            ...service,
            items,
          });
        }
      });
    }

    return services;
  }

  handleClose(isSuccess?: boolean, data: any = {}) {
    if (this.orderId && isSuccess) {
      this.dynamicDialogRef.close({ isSuccess, data: { openFrom: this.openFrom, assignedItems: this.helperService.deepCopy(this.salesCodeAssignedItemList) } });
    } else {
      this.dynamicDialogRef.close({ isSuccess });
    }
  }

  setNextSelectedItem(item: any) {
    if (!this.skippedItems[item?.itemId]?.isSkipped) {
      this.selectedItem = this.helperService.deepCopy(item);
      this.selectedItem.isSkipped = false;
    }
  }

  handleDiscardChanges() {
    if (this.salesCodeAssignedItemList?.length && this.isShowMultipleAssignView) {
      const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
        width: '30%',
        styleClass: 'small-heading-dialog',
        showHeader: false,
        data: {
          styleClassHeading: 'font-14',
          title: `Would you like to save changes?`,
          message: `There are unsaved changes here. Would you like to save them before leaving this page?`,
          button: {
            success: { title: 'Save changes and exit' },
            cancel: { title: 'Discard changes' },
          },
        },
      });

      const onClose = ref.onClose.subscribe(({ isSuccess }) => {
        if (isSuccess) {
          this.handleSaveAndExit();
        } else {
          this.handleClose();
        }
        onClose.unsubscribe();
      });
    } else {
      this.handleClose();
    }
  }

  setSkippedItems(item: any) {
    if (item?.itemId) {
      const itemId = item?.itemId;
      const itemsList: any = Object.values(this.skippedItems || {});
      const index = findIndex(itemsList, { itemId: itemId });
      if (!this.skippedItems[itemId]) {
        this.skippedItems[itemId] = { ...item, isSkipped: true };
      } else {
        this.skippedItems[itemId].isSkipped = true;
      }

      const isAllMarkSkipped = itemsList?.every((item: any) => item?.isSkipped);
      if (!isAllMarkSkipped) {
        if (index + 1 < itemsList?.length && !this.skippedItems[itemsList[index + 1]?.itemId]?.isSkipped) {
          this.setNextSelectedItem(this.skippedItems[itemsList[index + 1]?.itemId]);
        } else {
          //all items would be skipped;
          const obj = find(itemsList, { isSkipped: false });
          this.setNextSelectedItem(obj);
        }
      } else if (isAllMarkSkipped) {
        this.selectedItem = {};
        this.handleSaveAndExit();
      }
    }
  }

  getData() {
    this.isLoading = true;
    const queryParams = {
      fetchSalesGroup: true,
      limit: this.pageSize,
      page: 1,
    };
    this.getGroupedSalesCode(queryParams)
      .then(
        (res: any) => {
          if (res?.categories?.length) {
            this.groupedSalesResults = res?.categories[0]?.records || [];
            if (res?.categories[0]?.metaData) {
              this.totalRecords = res?.categories[0]?.metaData?.totalRecords || 0;
            }
          }
          this.salesGroupsResults = res?.salesGroups || [];
        },
        (err: any) => {
          this.helperService.showToaster('error', err);
        },
      )
      .finally(() => {
        this.isLoading = false;
        // if (this.groupedSalesResults?.length) {
        //     const groupedItems = this.groupedSalesResults.filter((salesGroup: any) => { return salesGroup?.salesCodes?.length });
        //     if(groupedItems?.length) {
        //         let saleGroupIdx = -1;
        //         groupedItems.forEach((groupItem: any) => {
        //             if(saleGroupIdx < 0) {
        //                 saleGroupIdx = findIndex(this.salesGroupsResults, {_id: groupItem?._id});
        //             }
        //         });

        //         if(saleGroupIdx > -1) {
        //             this.selectSaleGroup(saleGroupIdx);
        //         }
        //     }
        // }
      });
  }

  getGroupedSalesCodeObject(saleGroupId: string): any {
    const groupedObj = find(this.groupedSalesResults, { _id: saleGroupId });
    return groupedObj;
  }

  async getGroupedSalesCode(queryParams): Promise<any> {
    return this.httpService.getGroupedSalesCode(queryParams);
  }

  // async getUnassignedSalesCodeItems(orderId: string) {
  //     try {
  //       this.groupedSalesResults = await this.httpService.getUnassignedSalesCodeItems(orderId);
  //     } catch (err) {
  //       this.helperService.showToaster('error', err);
  //     }
  // }

  moveToActiveSalesCode(saleCodeDetail: any, key: string, activeTabIndex: number) {
    let index = findIndex(saleCodeDetail?.salesCodeItems, { isActive: true });
    let indexToActive = 0;
    if (key === 'DOWN') {
      if (index + 1 < saleCodeDetail?.salesCodeItems?.length) {
        indexToActive = index + 1;
      }
    }
    if (key === 'UP') {
      if (index - 1 >= 0 && index - 1 < saleCodeDetail?.salesCodeItems?.length) {
        indexToActive = index - 1;
      }
    }
    //this.selectSaleCode(saleCodeDetail || [], indexToActive);
    index = findIndex(saleCodeDetail?.salesCodeItems, { isActive: true });
    if (index < 0) {
      saleCodeDetail.salesCodeItems[0].isActive = true;
    }
    //console.log('test key = ', key);
    //console.log('test activeTabIndex = ', activeTabIndex);
  }

  scrollToTop() {
    setTimeout(() => {
      try {
        const element = document.getElementById('scrollTopSection');
        if (element) {
          element.scrollTop = 0;
        }
      } catch (err) {}
    }, 100);
  }

  resetToDefaultState() {
    this.resetActiveState(this.salesGroupsResults || []);
    this.searchQuery = '';
    this.isFocusSearch = true;
    this.scrollToTop();
    this._searchSalesGroup('');
  }

  selectSaleGroup(index: number) {
    this.resetActiveState(this.salesGroupsResults || []);

    this.salesGroupsResults[index].isActive = true;
    const groupedObj: any = this.getGroupedSalesCodeObject(this.salesGroupsResults[index]?._id);
    if (groupedObj?.salesCodes?.length) {
      //this.selectSaleCode(groupedObj, 0);
      this.scrollToTheGroup(this.salesGroupsResults[index]._id);
    }
  }

  scrollToTheGroup(groundId: string) {
    const element = document.getElementById(groundId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      element?.classList?.add('group-card-shadow');
    }
  }

  resetActiveState(itemsArray: any[]) {
    itemsArray?.forEach((item: any) => {
      item.isActive = false;
      const element = document.getElementById(item?._id);
      if (element) {
        element?.classList?.remove('group-card-shadow');
      }
    });
  }

  // selectSaleCode(saleCodeGroup: any, itemIndex: number) {
  //     this.groupedSalesResults?.forEach((group: any) => {
  //         this.resetActiveState(group?.salesCodes);
  //     });
  //     saleCodeGroup.salesCodes[itemIndex].isActive = true;

  // }

  handleSelectSaleCode(saleCodeGroup: any, itemIndex: number) {
    const salesCode = saleCodeGroup.salesCodes[itemIndex];
    if (this.isShowMultipleAssignView) {
      //do something in case of multi assign
      //this.selectSaleCode(saleCodeGroup, itemIndex);
      const item: any = this.helperService.deepCopy(this.selectedItem);
      if (!item.itemId) {
        return;
      }
      const serviceIdx = findIndex(this.unassignedServices, (service: any) => {
        return service?._id === this.selectedItem.serviceId;
      });
      if (serviceIdx > -1) {
        const index = findIndex(this.unassignedServices[serviceIdx]?.items, { itemId: this.selectedItem.itemId, isSkipped: false });
        if (index > -1) {
          this.unassignedServices[serviceIdx]?.items.splice(index, 1);
          delete this.skippedItems[this.selectedItem.itemId];

          if (!this.unassignedServices[serviceIdx]?.items.length) {
            this.unassignedServices.splice(serviceIdx, 1);
          }
        }
      }
      item.salesCode = salesCode;
      this.helperService.updateClassficationServiceItemTag(item.tags, { type: item?.type, category: item.category, salesCode: item.salesCode });
      delete item?.isSkipped;
      delete item?.itemClassificationTitle;
      this.salesCodeAssignedItemList.push(item);
      this.helperService.showToaster('success', 'Sales code assigned successfully');
      this.resetToDefaultState();
      if (this.unassignedServices?.length) {
        const itemsList: any = Object.values(this.skippedItems || {});
        const index = findIndex(itemsList, { itemId: this.selectedItem?.itemId, isSkipped: false });
        if (index + 1 < itemsList?.length && !this.skippedItems[itemsList[index + 1]?.itemId]?.isSkipped) {
          this.setNextSelectedItem(this.skippedItems[itemsList[index + 1]?.itemId]);
        } else {
          //all items would be skipped;
          const obj = find(itemsList, { isSkipped: false });
          if (obj) {
            this.setNextSelectedItem(obj);
          }
        }
      }

      if (this.salesCodeAssignedItemList?.length) {
        if (!this.unassignedServices?.length) {
          this.handleSaveAndExit();
        } else if (this.unassignedServices?.length && Object.values(this.skippedItems || {})?.length) {
          //check we have any skipped items
          let onlyHasSkippedItems: boolean = true;
          this.unassignedServices.forEach((service: any) => {
            service?.items?.forEach((item: any) => {
              if (!this.skippedItems[item.itemId]?.isSkipped) {
                onlyHasSkippedItems = false;
              }
            });
          });

          if (onlyHasSkippedItems) {
            this.handleSaveAndExit();
          }
        }
      }
    } else {
      delete salesCode.isActive;
      this.assignSalesCode(salesCode);
    }
  }

  assignSalesCode(salesCode: any) {
    this.originalItemObject.salesCode = salesCode;
    this.helperService.showToaster('success', 'Sales code assigned successfully');
    this.handleClose(true, { assigendCode: salesCode });
  }

  _searchSalesGroup(value: string, pageNumber: number = 1) {
    this.searchQuery = trim(value);
    this.isLoadingSearch = true;
    const query: any = {
      limit: this.pageSize,
      page: pageNumber,
    };
    if (this.searchQuery) {
      query.searchTerm = this.searchQuery;
    }
    this.getGroupedSalesCode(query)
      .then(
        (res: any) => {
          let categories = [];
          if (res?.categories[0].records?.length) {
            categories = res?.categories[0].records;
          }
          //for now until the pagination bug has not been till now below code should be commented
          //this.groupedSalesResults = [...this.groupedSalesResults,  ...categories];
          this.groupedSalesResults = categories || [];

          if (res?.categories[0]?.metaData) {
            this.totalRecords = res?.categories[0]?.metaData?.totalRecords || 0;
          }
        },
        (err: any) => {
          this.helperService.showToaster('error', err);
        },
      )
      .finally(() => {
        this.isLoadingSearch = false;
      });
  }

  makeSalesCodePayload(): any[] {
    const payload: any[] = this.salesCodeAssignedItemList?.map((item: any) => {
      const obj = {
        ...item,
      };
      if (obj.isSubItem) {
        obj.itemId = obj.flatRateId;
        delete obj.flatRateId;
        delete obj.isSubItem;
      }
      delete obj.isSkipped;
      return obj;
    });
    return payload;
  }

  async handleSaveAndExit() {
    if (this.salesCodeAssignedItemList?.length) {
      this.isSubmitting = true;
      try {
        const payload = this.makeSalesCodePayload();
        await this.httpService.assignSalesCodeToServiceItems(payload, this.orderId);
        this.salesCodeAssignedItemList.forEach((item: any) => {
          this.logPXGaingSightCustomEvent(item);
        });
        this.isSubmitting = false;
        this.handleClose(true);
      } catch (error) {
        this.helperService.showToaster('error', error);
      }
    } else {
      this.handleClose(true);
    }
  }

  onScroll = debounce((event: any) => {
    if (this.isLoadingSearch) return;

    const element = event.target;

    if (element.scrollHeight - element.scrollTop - element.clientHeight < 10) {
      if (this.totalRecords && this.groupedSalesResults?.length && this.groupedSalesResults?.length < this.totalRecords) {
        let pageNumber = Math.floor(this.groupedSalesResults?.length / this.pageSize) + 1;
        this.handleSearch(this.searchQuery || '', pageNumber);
      }
    }
  }, 100);
}
